import {
  faExclamationCircle,
  faRotateRight,
  faTableLayout,
} from "@fortawesome/pro-regular-svg-icons";
import { orderBy } from "lodash-es";

import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { useCaseTemplateWithVersions } from "src/cases/queries/templates";
import { ArchiveTemplateVersionModal } from "src/cases/templates/ArchiveTemplateVersionModal";
import { CaseTemplateVersionsTable } from "src/cases/templates/CaseTemplateVersionsTable";
import { DuplicateTemplateVersionModal } from "src/cases/templates/DuplicateTemplateVersionModal";
import { PublishTemplateVersionModal } from "src/cases/templates/PublishTemplateVersionModal";
import {
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel as CaseTemplateVersion,
  CaseTemplateVersionPublishState,
} from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { WhitePane } from "src/jobs/common/WhitePane";
import { PageHeader } from "src/layout/PageHeader";
import {
  CaseTemplateVersionsPageParams,
  getUrlToCaseQueuePage,
  getUrlToCaseTemplatesPage,
  getUrlToCaseTemplateVersionsPage,
} from "src/router/urls";
import { logger } from "src/utils/logger";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const CaseTemplateVersionsPage: React.FC = () => {
  const { orgId, wsId, templateId } =
    useParamsDecode<CaseTemplateVersionsPageParams>();

  const templateQuery = useCaseTemplateWithVersions(templateId);
  useDocumentTitle(["Case Template Versions"]);

  return (
    <div
      className="flex h-full flex-col"
      data-loc="case-template-versions-page"
    >
      <PageHeader
        breadcrumbs={[
          {
            key: "cases-page",
            breadcrumb: (
              <PageHeader.Breadcrumb
                dataLoc="cases-page"
                href={getUrlToCaseQueuePage({
                  wsId,
                  orgId,
                })}
                label="Cases"
                hideIcon
              />
            ),
          },
          {
            key: "case-templates",
            breadcrumb: (
              <PageHeader.Breadcrumb
                dataLoc="case-templates"
                href={getUrlToCaseTemplatesPage(orgId, wsId)}
                label="Case Templates"
              />
            ),
          },
          {
            key: "case-template-versions",
            breadcrumb: templateQuery.isLoading ? (
              <SkeletonPlaceholder height="h-4" width="w-24" />
            ) : (
              <PageHeader.Breadcrumb
                dataLoc="case-template-versions"
                href={getUrlToCaseTemplateVersionsPage(orgId, wsId, templateId)}
                label={
                  templateQuery.data?.attributes.template_name ??
                  "Unable to load template"
                }
                active
              />
            ),
          },
        ]}
      />

      <div className="decideScrollbar flex max-h-full min-w-[300px] overflow-x-auto p-5">
        <CaseTemplateVersionsContent templateId={templateId} />
      </div>
    </div>
  );
};

type CaseTemplateVersionsContentProps = {
  templateId: string;
};

const CaseTemplateVersionsContent: React.FC<
  CaseTemplateVersionsContentProps
> = ({ templateId }) => {
  const {
    data: template,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useCaseTemplateWithVersions(templateId);
  const {
    isOpen: isDuplicateModalOpen,
    data: versionIdToEdit,
    openModal: openDuplicateModal,
    closeModal: closeDuplicateModal,
  } = useModal<string>();
  const {
    isOpen: isArchiveModalOpen,
    data: versionToArchive,
    openModal: openArchiveModal,
    closeModal: closeArchiveModal,
  } = useModal<CaseTemplateVersion>();
  const {
    isOpen: isPublishModalOpen,
    data: versionToPublish,
    openModal: openPublishModal,
    closeModal: closePublishModal,
  } = useModal<CaseTemplateVersion>();

  const publishedVersions = orderBy(
    template?.versions?.filter(
      (version) =>
        !version.attributes.archived &&
        version.attributes.publish_state ===
          CaseTemplateVersionPublishState.PUBLISHED,
    ) ?? [],
    (version) => new Date(version.attributes.updated_at).valueOf(),
    "desc",
  );

  const draftVersions = orderBy(
    template?.versions?.filter(
      (version) =>
        !version.attributes.archived &&
        version.attributes.publish_state ===
          CaseTemplateVersionPublishState.DRAFT,
    ) ?? [],
    (version) => new Date(version.attributes.updated_at).valueOf(),
    "desc",
  );

  const handleDuplicate = (version: CaseTemplateVersion) => {
    openDuplicateModal(version.id);
  };

  const handleArchive = (version: CaseTemplateVersion) => {
    openArchiveModal(version);
  };

  const handlePublish = (version: CaseTemplateVersion) => {
    if (
      version.attributes.publish_state ===
      CaseTemplateVersionPublishState.PUBLISHED
    ) {
      logger.error("Cannot publish published version", version);
      return;
    }

    openPublishModal(version);
  };

  if (isError) {
    return (
      <WhitePane>
        <EmptyState
          actionButton={{
            icon: faRotateRight,
            text: "Retry",
            onClick: refetch,
            disabled: isFetching,
          }}
          description="We're having trouble loading case template versions. Please try again later."
          headline="Error loading versions"
          icon={faExclamationCircle}
          variant="error"
        />
      </WhitePane>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-5.5">
      {!isLoading &&
        publishedVersions.length === 0 &&
        draftVersions.length === 0 && (
          <WhitePane>
            <EmptyState
              description="No versions available. This is unexpected state, please contact support."
              headline="No versions found"
              icon={faTableLayout}
            />
          </WhitePane>
        )}

      {(publishedVersions.length > 0 || isLoading) && (
        <CaseTemplateVersionsTable
          isLoading={isLoading}
          versions={publishedVersions}
          onArchive={handleArchive}
          onDuplicate={handleDuplicate}
          onPublish={handlePublish}
        />
      )}

      {draftVersions.length > 0 && (
        <CaseTemplateVersionsTable
          isLoading={isLoading}
          versions={draftVersions}
          onArchive={handleArchive}
          onDuplicate={handleDuplicate}
          onPublish={handlePublish}
        />
      )}

      <DuplicateTemplateVersionModal
        open={isDuplicateModalOpen}
        versionId={versionIdToEdit}
        onClose={closeDuplicateModal}
      />

      <ArchiveTemplateVersionModal
        open={isArchiveModalOpen}
        templateId={templateId}
        version={versionToArchive ?? null}
        onClose={closeArchiveModal}
      />

      <PublishTemplateVersionModal
        open={isPublishModalOpen}
        templateId={templateId}
        version={versionToPublish ?? null}
        onClose={closePublishModal}
      />
    </div>
  );
};
